import {
  Collapsible as CollapsibleRoot,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@radix-ui/react-collapsible'
import {
  CheckCircle2Icon,
  ChevronDownIcon,
  ChevronUpIcon,
  TriangleAlertIcon,
} from 'lucide-react'
import { useState } from 'react'

import { type ComplianceCalculations } from 'components/hooks/quotes/useComplianceCalculations'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { Tooltip } from 'slab'
import { formatEmployeeContributionPercentage } from 'utils/quote_calculations'

type Props = {
  maxEmployeeContributionPercentage?: number;
  isLoading: boolean;
} & Pick<
  ComplianceCalculations,
  'employeesWithUnaffordablePlans' | 'ratingAreasWithUnfairAllowances'
>

export default function ComplianceChecks ({
  maxEmployeeContributionPercentage,
  employeesWithUnaffordablePlans,
  ratingAreasWithUnfairAllowances,
  isLoading,
}: Props) {
  const [open, setOpen] = useState(false)

  if (isLoading) {
    return (
      <div className='flex flex-col gap-2'>
        <SkeletonItem heightClass='h-4' widthClass='w-full' />
        <SkeletonItem heightClass='h-4' widthClass='w-full' />
      </div>
    )
  }

  const totalChecks = 2
  const passedChecks =
    (employeesWithUnaffordablePlans.length === 0 ? 1 : 0) +
    (ratingAreasWithUnfairAllowances.length === 0 ? 1 : 0)
  const allChecksPassed = passedChecks === totalChecks

  return (
    <CollapsibleRoot
      className='flex flex-col'
      open={open}
      onOpenChange={setOpen}
    >
      <CollapsibleTrigger asChild>
        <button
          className='flex items-center gap-2 text-sm cursor-pointer outline outline-2 outline-transparent focus-visible:outline-thatch-500 rounded-md'
          type='button'
        >
          <div className='flex items-center'>
            {open
              ? (
                <ChevronUpIcon className='w-4 h-4' />
                )
              : (
                <ChevronDownIcon className='w-4 h-4' />
                )}
          </div>
          <span>
            {passedChecks}/{totalChecks} Compliance checks passed
          </span>
          {allChecksPassed
            ? (
              <CheckCircle2Icon className='w-4 h-4 text-green-400' />
              )
            : (
              <TriangleAlertIcon className='w-4 h-4 text-red-400' />
              )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className='data-[state=open]:animate-collapsible-open data-[state=closed]:animate-collapsible-close'>
        <div className='flex flex-col gap-2 pt-2'>
          <div className='flex items-center gap-2'>
            {employeesWithUnaffordablePlans.length === 0
              ? (
                <CheckCircle2Icon className='w-4 h-4 text-green-400' />
                )
              : (
                <TriangleAlertIcon className='w-4 h-4 text-red-400' />
                )}
            <Tooltip
              content={`In order for an allowance to be considered affordable, employees must be able to purchase the lowest cost silver plan without paying more than ${formatEmployeeContributionPercentage(
                maxEmployeeContributionPercentage
              )}% of their annual income.`}
            >
              <span className='text-sm cursor-help'>
                {employeesWithUnaffordablePlans.length === 0
                  ? 'All employees have affordable coverage'
                  : `${employeesWithUnaffordablePlans.length} employee${
                      employeesWithUnaffordablePlans.length === 1
                        ? ' has'
                        : 's have'
                    } unaffordable coverage`}
              </span>
            </Tooltip>
          </div>
          <div className='flex items-center gap-2'>
            {ratingAreasWithUnfairAllowances.length === 0
              ? (
                <CheckCircle2Icon className='w-4 h-4 text-green-400' />
                )
              : (
                <TriangleAlertIcon className='w-4 h-4 text-red-400' />
                )}
            <Tooltip content="In order for an allowance to be considered fair, the oldest employee's allowance can be no more than 3x the youngest employee's allowance in a given rating area.">
              <span className='text-sm cursor-help'>
                {ratingAreasWithUnfairAllowances.length === 0
                  ? 'Fair allowance rule'
                  : `${ratingAreasWithUnfairAllowances.length} rating area${
                      ratingAreasWithUnfairAllowances.length === 1
                        ? ' has'
                        : 's have'
                    } an unfair allowance structure`}
              </span>
            </Tooltip>
          </div>
        </div>
      </CollapsibleContent>
    </CollapsibleRoot>
  )
}
